import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  YearReviewData,
  YearReviewInsights,
  MonthMoodType,
  MonthMoods,
  GrowthSuperpowerType,
  StoryKey,
} from '../types'
import { debouncedSaveJourney } from '../services/api'
import { useUserStore } from './useUserStore'

interface YearReviewState {
  yearReview: YearReviewData | null
  isCompleted: boolean
  isGeneratingInsights: boolean

  // Actions
  setHighlight: (key: keyof YearReviewData['highlights'], value: string) => void
  setLifeAreaRating: (areaId: string, rating: number) => void
  setStory: (key: StoryKey, value: string) => void
  setAiInsights: (insights: YearReviewInsights) => void
  setGeneratingInsights: (generating: boolean) => void
  complete: () => void
  reset: () => void

  // New tap-based section actions
  setMonthMood: (month: keyof MonthMoods, mood: MonthMoodType | null) => void
  addCountry: (countryCode: string) => void
  removeCountry: (countryCode: string) => void
  setFavoriteCountry: (countryCode: string | null) => void
  toggleGrowthSuperpower: (superpower: GrowthSuperpowerType) => void

  // Sync functions for D1
  loadFromServer: (data: YearReviewData | null) => void
  getSnapshot: () => YearReviewData | null
  syncToDatabase: () => void
}

const initialHighlights = {
  bestMemory: '',
  unexpectedWin: '',
  favoritePlace: '',
  personWhoPoweredYou: '',
  songOfTheYear: '',
  lessonLearned: '',
}

const initialStory = {
  yearStartedWith: '',
  turningPoint: '',
  proudOf: '',
  wordToDescribe: '',
}

const LIFE_AREA_IDS = ['health', 'relationships', 'career', 'creativity', 'growth', 'finances']

const initialMonthMoods: MonthMoods = {
  jan: null,
  feb: null,
  mar: null,
  apr: null,
  may: null,
  jun: null,
  jul: null,
  aug: null,
  sep: null,
  oct: null,
  nov: null,
  dec: null,
}

const initialYearReview: YearReviewData = {
  highlights: initialHighlights,
  lifeAreas: LIFE_AREA_IDS.map((id) => ({ area: id, rating: 0 })),
  monthMoods: initialMonthMoods,
  travelMemories: { countriesVisited: [], favoriteCountry: null },
  growthSuperpowers: [],
  story: initialStory,
}

export const useYearReviewStore = create<YearReviewState>()(
  persist(
    (set, get) => ({
      yearReview: null,
      isCompleted: false,
      isGeneratingInsights: false,

      setHighlight: (key, value) =>
        set((state) => ({
          yearReview: {
            ...(state.yearReview || initialYearReview),
            highlights: {
              ...(state.yearReview?.highlights || initialHighlights),
              [key]: value,
            },
          },
        })),

      setLifeAreaRating: (areaId, rating) =>
        set((state) => ({
          yearReview: {
            ...(state.yearReview || initialYearReview),
            lifeAreas: (state.yearReview?.lifeAreas || initialYearReview.lifeAreas).map((la) =>
              la.area === areaId ? { ...la, rating } : la
            ),
          },
        })),

      setStory: (key, value) =>
        set((state) => ({
          yearReview: {
            ...(state.yearReview || initialYearReview),
            story: {
              ...(state.yearReview?.story || initialStory),
              [key]: value,
            },
          },
        })),

      // Month mood actions
      setMonthMood: (month, mood) =>
        set((state) => ({
          yearReview: {
            ...(state.yearReview || initialYearReview),
            monthMoods: {
              ...(state.yearReview?.monthMoods || initialMonthMoods),
              [month]: mood,
            },
          },
        })),

      // Travel memory actions
      addCountry: (countryCode) =>
        set((state) => {
          const current = state.yearReview?.travelMemories?.countriesVisited || []
          if (current.includes(countryCode)) return state
          return {
            yearReview: {
              ...(state.yearReview || initialYearReview),
              travelMemories: {
                countriesVisited: [...current, countryCode],
                favoriteCountry: state.yearReview?.travelMemories?.favoriteCountry || null,
              },
            },
          }
        }),

      removeCountry: (countryCode) =>
        set((state) => {
          const current = state.yearReview?.travelMemories?.countriesVisited || []
          const favoriteCountry = state.yearReview?.travelMemories?.favoriteCountry
          return {
            yearReview: {
              ...(state.yearReview || initialYearReview),
              travelMemories: {
                countriesVisited: current.filter((c) => c !== countryCode),
                // Clear favorite if it was removed
                favoriteCountry: favoriteCountry === countryCode ? null : (favoriteCountry ?? null),
              },
            },
          }
        }),

      setFavoriteCountry: (countryCode) =>
        set((state) => ({
          yearReview: {
            ...(state.yearReview || initialYearReview),
            travelMemories: {
              countriesVisited: state.yearReview?.travelMemories?.countriesVisited || [],
              favoriteCountry: countryCode,
            },
          },
        })),

      // Growth superpower actions
      toggleGrowthSuperpower: (superpower) =>
        set((state) => {
          const current = state.yearReview?.growthSuperpowers || []
          const isSelected = current.includes(superpower)
          let newSuperpowers: GrowthSuperpowerType[]

          if (isSelected) {
            // Remove it
            newSuperpowers = current.filter((s) => s !== superpower)
          } else if (current.length < 3) {
            // Add it (max 3)
            newSuperpowers = [...current, superpower]
          } else {
            // Already at max, don't add
            return state
          }

          return {
            yearReview: {
              ...(state.yearReview || initialYearReview),
              growthSuperpowers: newSuperpowers,
            },
          }
        }),

      setAiInsights: (insights) =>
        set((state) => ({
          yearReview: state.yearReview
            ? { ...state.yearReview, aiInsights: insights }
            : null,
        })),

      setGeneratingInsights: (generating) =>
        set({ isGeneratingInsights: generating }),

      complete: () => {
        set((state) => ({
          yearReview: state.yearReview
            ? { ...state.yearReview, completedAt: new Date().toISOString() }
            : null,
          isCompleted: true,
        }))
        // Sync to database after completing
        get().syncToDatabase()
      },

      reset: () => set({ yearReview: null, isCompleted: false }),

      // Load data from server
      loadFromServer: (data) => {
        if (data) {
          set({
            yearReview: data,
            isCompleted: !!data.completedAt,
          })
        }
      },

      // Get current state for syncing
      getSnapshot: () => get().yearReview,

      // Sync current state to database
      syncToDatabase: () => {
        const { userId } = useUserStore.getState()
        const year = new Date().getFullYear()

        if (!userId) {
          console.warn('syncToDatabase skipped: no userId')
          return
        }

        const snapshot = get().getSnapshot()
        if (snapshot) {
          debouncedSaveJourney(userId, year, {
            yearReview: snapshot,
          })
        }
      },
    }),
    {
      name: `year-wrapped-year-review-${new Date().getFullYear()}`,
    }
  )
)
